import { useCallback, useEffect, useMemo, useState } from "react";

import { Marker, useMap } from "react-leaflet";
import { Icon, LatLngTuple } from "leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";

import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { GeoCodeInput, GeoCodeResult } from "@covertree/new-business-graphql";
import {
  selectGeoCodeLoading,
  selectGeoCodeResult,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Button } from "@covertree/ui";
import { Modal } from "@covertree/ui";
import { Map } from "@covertree/ui/map";
import IconButton from "@covertree/ui/buttons/IconButton";
import { colors } from "@covertree/theme/colors";

import { PreciselyAddressWithUnit } from "../autocompletePrecisely";

const icon = new Icon({
  iconUrl: markerIconPng,
  iconSize: [25, 41],
  iconAnchor: [12.5, 41],
  popupAnchor: [0, -41],
});

function MapContent({ position }: { position: LatLngTuple }) {
  const map = useMap();

  useEffect(() => {
    map.flyTo(position, map.getMaxZoom(), { animate: false });
  }, [map, position]);

  return <Marker icon={icon} position={position} />;
}

interface Props {
  isValid: boolean;
  onOpen?: (geoCodeInput: GeoCodeInput) => void;
  address?: PreciselyAddressWithUnit | null;
  isNewPolicyFlow?: boolean; // outside of the new policy flow we don't use redux to get the data
  result?: GeoCodeResult; // result from getting geoCode outside of the new policy flow
}

export function ViewEnteredAddressOnMap({
  isValid,
  onOpen,
  address,
  isNewPolicyFlow = false,
  result,
}: Props) {
  const geoCodeLoading = !isNewPolicyFlow
    ? useNewBusinessSelector(selectGeoCodeLoading)
    : undefined;
  const geoCodeData = !isNewPolicyFlow
    ? useNewBusinessSelector(selectGeoCodeResult)
    : undefined;

  const data = useMemo(
    () => (isNewPolicyFlow ? result : geoCodeData),
    [result, geoCodeData]
  );

  const [openView, setOpenView] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);

  const toggleOpen = useCallback(
    (openView?: boolean) => setOpenView((value) => openView ?? !value),
    [setOpenView]
  );

  useEffect(() => {
    if (!data && !geoCodeLoading && openView) return setOpenErrorModal(true);
    return setOpenErrorModal(false);
  }, [data, geoCodeLoading, openView]);

  const handleOpen = useCallback(async () => {
    if (!address && !result) return;

    toggleOpen(true);

    if (onOpen && address)
      onOpen({
        streetAddress: address.mainAddressLine,
        state: address.state,
        city: address.city,
        zipCode: address.postCode,
        ...(address.lotUnit
          ? {
              unitNumber: address.lotUnit,
            }
          : {}),
      });
  }, [address, toggleOpen]);

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={handleOpen}
        disabled={!isValid}
        startIcon={<MapIcon />}>
        Satellite View
      </Button>

      <Dialog open={openView} onClose={() => toggleOpen()}>
        <IconButton
          data-testid="drawer-close-button"
          sx={(theme) => ({
            width: theme.spacing(5),
            height: theme.spacing(5),
            color: colors.green3,
            background: colors.white,
            border: `2px solid ${colors.green3}`,
            "&:hover": {
              color: colors.white,
              backgroundColor: colors.green3,
            },
            position: "absolute",
            top: 15,
            right: 15,
            zIndex: 1000,
          })}
          onClick={() => toggleOpen()}>
          <CloseIcon />
        </IconButton>

        <Box width={600} height="50vh">
          <Map>
            <Backdrop open={!!geoCodeLoading} sx={{ zIndex: 1000 }}>
              <CircularProgress color="secondary" size={60} />
            </Backdrop>
            {data && <MapContent position={[data.latitude, data.longitude]} />}
          </Map>
        </Box>
      </Dialog>

      <Modal
        title="Oops!"
        maxWidth="md"
        open={openErrorModal}
        onClose={() => setOpenErrorModal(false)}
        image={
          <img
            src="/images/icons/cross.webp"
            alt="error"
            width={100}
            height={100}
          />
        }>
        <Typography gutterBottom>
          It looks like we couldn&#39;t find this address on the map.
        </Typography>
      </Modal>
    </>
  );
}
